// src\components\portfolio\ImagePreviewModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Zoom, Keyboard } from 'swiper/modules';
import {
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiMaximize2,
  FiMinimize2,
  FiImage,
  FiInfo,
  FiDownload,
  FiShare2,
  FiHeart,
} from 'react-icons/fi';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';

const ImagePreviewModal = ({
  isOpen,
  onClose,
  images,
  initialIndex = 0,
  project = null,
  onLike = null,
  likesCount = 0,
  isLiked = false,
  isLiking = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [imagesLoaded, setImagesLoaded] = useState({});
  const swiperRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setCurrentIndex(initialIndex);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, initialIndex]);

  const handleImageError = index => {
    setImageErrors(prev => ({ ...prev, [index]: true }));
  };

  const handleImageLoad = index => {
    setImagesLoaded(prev => ({ ...prev, [index]: true }));
  };

  const handleSlideChange = swiper => {
    setCurrentIndex(swiper.activeIndex);
  };

  const handleDownload = (url, index) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `project-image-${index + 1}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShare = async () => {
    if (!project) return;
    try {
      const shareData = {
        title: project.title,
        text: `Check out this project: ${project.title}`,
        url: window.location.href,
      };
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const getOptimizedImageUrl = (url, width = 1200) => {
    if (!url) return '';
    if (url.includes('cloudinary.com')) {
      return url.replace(
        '/upload/',
        `/upload/w_${width},q_auto:best,f_auto,c_limit,g_center,dpr_auto/`,
      );
    }
    return url;
  };

  const handleKeyDown = e => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') {
      swiperRef.current?.slidePrev();
    }
    if (e.key === 'ArrowRight') {
      swiperRef.current?.slideNext();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!isOpen || !images || images.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.95)' }}
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-20 p-2 rounded-full transition-all hover:scale-110 hover:bg-white/10"
        style={{ color: 'white' }}
        aria-label="Close preview"
      >
        <FiX className="w-6 h-6" />
      </button>

      {/* Top Controls */}
      <div className="absolute top-4 left-4 z-20 flex items-center gap-2">
        <span className="px-3 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs font-medium">
          {currentIndex + 1} / {images.length}
        </span>
        {project && (
          <span className="px-3 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs font-medium truncate max-w-[150px]">
            {project.title}
          </span>
        )}
      </div>

      {/* Top Right Controls */}
      <div className="absolute top-4 right-16 z-20 flex items-center gap-2">
        <button
          onClick={() => setIsZoomed(!isZoomed)}
          className="p-2 rounded-full transition-all hover:scale-110 hover:bg-white/10"
          style={{ color: 'white' }}
          aria-label={isZoomed ? 'Zoom out' : 'Zoom in'}
        >
          {isZoomed ? (
            <FiMinimize2 className="w-5 h-5" />
          ) : (
            <FiMaximize2 className="w-5 h-5" />
          )}
        </button>
        {project && (
          <>
            <button
              onClick={handleShare}
              className="p-2 rounded-full transition-all hover:scale-110 hover:bg-white/10"
              style={{ color: 'white' }}
              aria-label="Share"
            >
              <FiShare2 className="w-5 h-5" />
            </button>
            {onLike && (
              <button
                onClick={onLike}
                disabled={isLiking}
                className={`p-2 rounded-full transition-all hover:scale-110 ${
                  isLiking ? 'opacity-50 cursor-wait' : ''
                }`}
                style={{
                  color: isLiked ? '#ef4444' : 'white',
                  backgroundColor: isLiked
                    ? 'rgba(239,68,68,0.2)'
                    : 'transparent',
                }}
                aria-label={isLiked ? 'Unlike' : 'Like'}
              >
                {isLiking ? (
                  <div
                    className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                    style={{ borderColor: isLiked ? '#ef4444' : 'white' }}
                  />
                ) : (
                  <FiHeart
                    className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`}
                  />
                )}
                <span className="sr-only">{likesCount} likes</span>
              </button>
            )}
          </>
        )}
      </div>

      {/* Main Image */}
      <div className="w-full h-full max-w-6xl max-h-[90vh] relative">
        <Swiper
          modules={[Navigation, Pagination, Zoom, Keyboard]}
          navigation={{
            nextEl: '.modal-swiper-button-next',
            prevEl: '.modal-swiper-button-prev',
          }}
          pagination={{
            clickable: true,
            dynamicBullets: true,
            type: 'bullets',
          }}
          zoom={{
            maxRatio: 3,
            minRatio: 1,
          }}
          keyboard={{
            enabled: true,
          }}
          initialSlide={initialIndex}
          onSlideChange={handleSlideChange}
          onSwiper={swiper => {
            swiperRef.current = swiper;
          }}
          className="modal-image-swiper"
          style={{ width: '100%', height: '100%' }}
        >
          {images.map((image, index) => {
            const imageUrl = typeof image === 'string' ? image : image.url;
            const isError = imageErrors[index];
            const isLoaded = imagesLoaded[index];
            const altText = project?.title
              ? `${project.title} - Image ${index + 1}`
              : `Image ${index + 1}`;

            return (
              <SwiperSlide key={index}>
                <div className="flex items-center justify-center w-full h-full p-8">
                  {!isLoaded && !isError && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div
                        className="w-12 h-12 border-2 border-t-transparent rounded-full animate-spin"
                        style={{ borderColor: 'white' }}
                      />
                    </div>
                  )}
                  {isError ? (
                    <div className="flex flex-col items-center justify-center text-white/50">
                      <FiImage className="w-16 h-16 mb-3" />
                      <p className="text-sm">Unable to load image</p>
                    </div>
                  ) : (
                    <div className="swiper-zoom-container w-full h-full flex items-center justify-center">
                      <img
                        src={getOptimizedImageUrl(imageUrl, 1200)}
                        alt={altText}
                        className={`transition-opacity duration-500 ${
                          isLoaded ? 'opacity-100' : 'opacity-0'
                        }`}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                        onLoad={() => handleImageLoad(index)}
                        onError={() => handleImageError(index)}
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>

        {/* Navigation Buttons */}
        {images.length > 1 && (
          <>
            <button
              className="modal-swiper-button-prev absolute left-2 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full transition-all hover:scale-110 hover:bg-white/20"
              style={{ backgroundColor: 'rgba(0,0,0,0.5)', color: 'white' }}
              aria-label="Previous image"
            >
              <FiChevronLeft className="w-6 h-6" />
            </button>
            <button
              className="modal-swiper-button-next absolute right-2 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full transition-all hover:scale-110 hover:bg-white/20"
              style={{ backgroundColor: 'rgba(0,0,0,0.5)', color: 'white' }}
              aria-label="Next image"
            >
              <FiChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        {/* Bottom Info */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3 px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs">
          <span>
            {currentIndex + 1} / {images.length}
          </span>
          <span className="w-px h-4 bg-white/30" />
          <span>Use arrow keys to navigate</span>
          <span className="w-px h-4 bg-white/30" />
          <button
            onClick={() => {
              const url =
                typeof images[currentIndex] === 'string'
                  ? images[currentIndex]
                  : images[currentIndex]?.url;
              if (url) handleDownload(url, currentIndex);
            }}
            className="flex items-center gap-1 hover:text-blue-400 transition-colors"
          >
            <FiDownload className="w-3 h-3" />
            <span>Download</span>
          </button>
        </div>
      </div>

      <style jsx>{`
        .modal-image-swiper {
          width: 100%;
          height: 100%;
        }
        .modal-image-swiper .swiper-slide {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          width: 100%;
        }
        .modal-image-swiper .swiper-wrapper {
          height: 100%;
        }
        .modal-image-swiper .swiper-zoom-container {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .modal-image-swiper .swiper-pagination {
          position: absolute;
          bottom: 80px;
          z-index: 10;
        }
        .modal-image-swiper .swiper-pagination-bullet {
          background: white;
          opacity: 0.5;
          width: 8px;
          height: 8px;
        }
        .modal-image-swiper .swiper-pagination-bullet-active {
          opacity: 1;
          width: 20px;
          border-radius: 4px;
          background: white;
        }
        .modal-image-swiper .swiper-button-prev,
        .modal-image-swiper .swiper-button-next {
          display: none;
        }
        .modal-swiper-button-prev,
        .modal-swiper-button-next {
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
    </div>
  );
};

export default ImagePreviewModal;
